import { InlineRichText } from '@nextiva/rich-text-editor';
import { alignedQuestion } from '@nextiva/engagement-ui';
import { compose } from 'recompose';
import { Field } from 'redux-form';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { debouncedReduxForm } from 'sharedParts';

import CommentsField from 'components/CommentsField';
import StylingToCSSLabelText from 'styling/hocs/StylingToCSS/StylingToCSSLabelText';
import isQuestionCommentsDisabled from 'utils/isQuestionCommentsDisabled';
import propTypeBlockStyling from 'styling/config/editorBlock/PropTypes/blockStyling';
import propTypeStyleLabel from 'styling/config/editorBlock/PropTypes/styleLabel';
import StylingTextOptionToRTEProps from 'styling/hocs/StylingToRTEProps/StylingTextOptionToRTEProps';

import DropdownQuestionFormField from '../DropdownQuestionFormField';
import getRichContentFromOptionModel from '../../utils/getRichContentFromOptionModel';
import styles from './DropdownQuestionForm.scss';
import withTakerValidator from '../../hoc/withTakerValidator';

class DropdownQuestionForm extends Component {
  static propTypes = {
    block: PropTypes.shape({
      required: PropTypes.bool,
      options: PropTypes.array.isRequired,
      label: PropTypes.string,
      labelEnabled: PropTypes.bool,
    }).isRequired,
    cancelSubmit: PropTypes.func.isRequired,
    flushSubmit: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
    inlineEditTool: PropTypes.node,
    validator: PropTypes.arrayOf(PropTypes.func),
    defaultFontSize: PropTypes.number.isRequired,
    defaultFont: PropTypes.string.isRequired,
    defaultFontColor: PropTypes.string.isRequired,
    defaultBold: PropTypes.bool.isRequired,
    defaultItalic: PropTypes.bool.isRequired,
    defaultUnderline: PropTypes.bool.isRequired,
    styleLabel: propTypeStyleLabel,
    styling: propTypeBlockStyling,
  };

  static defaultProps = {
    inlineEditTool: null,
  };

  componentWillUnmount() {
    this.props.cancelSubmit();
  }

  emptyOptionList = [];

  stopNativeEventPropagation = e => e.nativeEvent.stopImmediatePropagation();

  handleSubmit = () => {
    if (this.props.readOnly) {
      return;
    }
    this.submit();
  };

  hasLabel = option => !!option.value.trim();

  renderRichText = option => {
    const {
      defaultFontSize,
      defaultFont,
      defaultFontColor,
      defaultBold,
      defaultItalic,
      defaultUnderline,
    } = this.props;
    return (
      // @note fix options selection in iOS device (approach is taken from next-ui implementation) @feshchenko
      <div onTouchStart={this.stopNativeEventPropagation}>
        <InlineRichText
          className={styles.item}
          value={getRichContentFromOptionModel(option)}
          defaultFontSize={defaultFontSize}
          defaultFont={defaultFont}
          defaultFontColor={defaultFontColor}
          defaultBold={defaultBold}
          defaultItalic={defaultItalic}
          defaultUnderline={defaultUnderline}
        />
      </div>
    );
  };

  render() {
    const {
      block: { labelEnabled, options, label, commentsEnabled, commentsLabel },
      readOnly,
      inlineEditTool,
      flushSubmit,
      validator,
      styleLabel,
      styling,
    } = this.props;

    const labelBody = <span style={styleLabel}>{label}</span>;

    return (
      <div>
        <Field
          name="value"
          component={DropdownQuestionFormField}
          validate={validator}
          props={{
            className: readOnly ? styles.readOnly : styles.container,
            placeholder: null,
            labelKey: 'value',
            valueKey: 'id',
            label: labelEnabled ? labelBody : null,
            optionRenderer: this.renderRichText,
            options: readOnly
              ? this.emptyOptionList
              : options.filter(this.hasLabel),
            clearable: false,
            searchable: !readOnly,
          }}
        />
        {inlineEditTool}
        {!readOnly && (
          <Field
            name="comments"
            component={CommentsField}
            onBlur={flushSubmit}
            props={{
              readOnly,
              className: styles.comments,
              disabled: isQuestionCommentsDisabled(this.props),
              commentsEnabled,
              commentsLabel,
              styling,
            }}
          />
        )}
      </div>
    );
  }
}

export default compose(
  alignedQuestion,
  debouncedReduxForm({
    form: 'DropdownQuestionForm',
    isAutoSubmit: true,
  }),
  withTakerValidator,
  StylingTextOptionToRTEProps,
  StylingToCSSLabelText,
)(DropdownQuestionForm);
